import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import JoditEditor, { Jodit } from 'jodit-react'
import 'jodit/es2021/jodit.min.css'
import 'jodit/es2021/plugins/speech-recognize/speech-recognize'
import 'jodit/es2021/plugins/speech-recognize/speech-recognize.css'
import { useWindowDimensions } from '../hooks/useWindowDimensions'
import FolderExplorer, { FolderNode, FolderNodeUI, getAPINodeById, getNodeById, selectFolderNode, toggleFolderNode } from './folders'
import type { IJodit } from 'jodit/esm/types'
import ServiceEditor, { EditorNodeType, IEditorNode } from '../Services/ServiceEditor'
import { SplitView } from '../splitview/SplitView'
import Util from '../Util'
import { DataResponseStatusIds, EmptyId, GUID } from '../Services/ServiceTypes'
import { ErrorDialog } from '../dialog/errorDialog'
import { ConfirmRemoveFolderDialog } from '../dialog/confirmRemoveFolderDialog'
import { RenameNodeDialog } from '../dialog/renameNodeDialog'
import { CreateFolderNodeDialog } from '../dialog/createFolderNodeDialog'
import { CreateItemNodeDialog } from '../dialog/createItemNodeDialog'
import Consts from '../Const'
import { NodeAttachmentsDialog } from '../dialog/nodeAttachmentsDialog'
import { ConfirmRemoveAttachmentDialog, IEditorAttachmentToBeRemoved } from '../dialog/confirmRemoveAttachmentDialog'
import { IPlayer } from '../Services/ServiceSecurity'
import { ChangePasswordDialog } from '../dialog/changePasswordDialog'
import ServicePlayer from '../Services/ServicePlayer'
import { lastOpenItemNodeStore } from '../storage/lastOpenItemNodeStore'
import { lastOpenFolderNodesStore } from '../storage/lastOpenFolderNodesStore'
import useDidMountEffect from '../hooks/useDidMountEffect'
import { ConfirmSetFolderItemUnlockedDialog } from '../dialog/confirmSetFolderItemUnlockedDialog'
import { WooNotesButton, WooNotesButtonColor, WooNotesButtonSize } from './WooNotesButton'
import { useElementDimensions } from '../hooks/useElementDimensions'
import { WooNotesPopupMenu } from './WooNotesPopupMenu'
import { useGlobalCtrlAndKeypress } from '../hooks/useGlobalCtrlAndKeypress'
import './WooEditorAndNodes.css'
import { getCaretIndex, TextCaret } from './getCaretIndexWithSeparators'
import { buildEditorConfig } from './WooEditorHelper'
import { Unauthorized } from '../Errors/Unauthorized'
import { IsUnauthorizedDialog } from '../dialog/IsUnauthorizedDialog'
import { ConfirmMoveNodeDialog } from '../dialog/confirmMoveNodeDialog'
import { useBeforeUnload } from '../hooks/useBeforeUnload'
import { WooNotesTextInput } from './WooNotesTextInput'
import { LoaderOverlay } from './LoaderOverlay'
import { useDelayedLoading } from '../hooks/useDelayedLoading'
import { mapEditorNodesToFolderNodes, mapEditorNodeToFolderNodeBase, ViewStyle } from './mapEditorNodesToFolderNodes'
import { NodeHistoryDialog } from '../dialog/nodeHistoryDialog'
import { lastSplitViewLeftWidthStore } from '../storage/lastSplitViewLeftWidthStore'

interface IWooEditorAndNodesProps {
  onLogout?: () => void
  token: string
  player: IPlayer | undefined
}

export const getOpenNodeIds = (nodes: FolderNode[]): string[] => {
  const result: string[] = []

  const traverse = (items: FolderNode[]) => {
    for (const node of items) {
      if (node.isOpen) {
        result.push(node.id)
      }

      if (node.children && node.children.length > 0) {
        traverse(node.children)
      }
    }
  }

  traverse(nodes)

  return result
}

const WooEditorAndNodes = ({ onLogout, token: tokenX, player: playerX }: IWooEditorAndNodesProps) => {
  const { width, height } = useWindowDimensions()

  const [token, setToken] = useState(tokenX)
  const [player, setPlayer] = useState(playerX)

  const [allTags, setAllTags] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set<string>())

  const [nodesRead, setNodesRead] = useState(false)
  const [nodes, setNodes] = useState<IEditorNode[] | undefined>(undefined)
  const [reloadNodes, setReloadNodes] = useState(0)
  const lastReloadedNodesRef = useRef<number>(-1)
  const [folderNodes, setFolderNodes] = useState<FolderNode[]>([])
  const [defaultSelectedNodeId, setDefaultSelectedNodeId] = useState<GUID | null>(null)

  const searchTimeoutRef = useRef<number | null>(null)
  const [searchForText, setSearchForText] = useState('')

  const persistLeftWidthRef = useRef<number | null>(null)
  const [persistLeftWidth, setPersistLeftWidth] = useState(() => lastSplitViewLeftWidthStore.get() ?? 320)
  useDidMountEffect(() => {
    lastSplitViewLeftWidthStore.set(persistLeftWidth)
  }, [persistLeftWidth])

  const [viewStyle, setViewStyle] = useState<ViewStyle>('byFolder')
  const changeViewStyle = () => {
    const newViewStyle: ViewStyle = viewStyle === 'byFolder' ? 'sortedDesc' : 'byFolder'
    setViewStyle(newViewStyle)
  }

  const buildNodePath = useCallback(
    (nodeId: string | undefined) => {
      if (!nodes) return ''

      const nodeLookup = new Map<string, IEditorNode>(nodes.map(n => [n.Id, n]))

      let nId: string | undefined = nodeId
      const path = [] as string[]
      while (nId !== undefined) {
        const node = nodeLookup.get(nId)
        if (node) {
          path.push(node.Name)
        }

        nId = node?.ParentNodeId
      }

      const finalPath = path.reverse().slice(0, path.length - 1)
      if (finalPath.length === 0) return 'root'
      return finalPath.join(' / ')
    },
    [nodes],
  )

  useGlobalCtrlAndKeypress(key => {
    switch (key) {
      case 's':
        doSave()
        return true
      case 'l':
        doTryToggleLock()
        return true
    }
    return false
  })

  const setupFolderNodes = (nodes: IEditorNode[], viewStyle: ViewStyle) => {
    const folderOpenIdsData = lastOpenFolderNodesStore.get()
    const folderOpenIds = new Set<GUID>(folderOpenIdsData)

    const filteredNodes = (nodes ?? []).filter(node => {
      if (node.Type === EditorNodeType.Folder) return true
      if (!selectedTags.size) return true

      return node.Tags.filter(tag => selectedTags.has(tag)).length === selectedTags.size
    })

    const xxx = mapEditorNodesToFolderNodes(filteredNodes, folderOpenIds, viewStyle)
    const lastOpenItemNodeId = lastOpenItemNodeStore.get()
    setFolderNodes(selectFolderNode(xxx, lastOpenItemNodeId))

    setSelectedFolderNode(existing => {
      for (const node of filteredNodes) {
        if (node.Id === existing?.id) {
          if (node.Locked !== existing.isLocked) {
            // TODO Add function if more settings would require the selectedNoneFolderNode to be refreshed
            return mapEditorNodeToFolderNodeBase(node)
          }
          return existing
        }
        if (node.Id === lastOpenItemNodeId) {
          return mapEditorNodeToFolderNodeBase(node)
        }
      }
      return undefined
    })
    setDefaultSelectedNodeId(lastOpenItemNodeId)
    setNodesRead(true)
  }

  useEffect(() => {
    if (lastReloadedNodesRef.current === reloadNodes) return
    lastReloadedNodesRef.current = reloadNodes

    if (!token) {
      setNodes([])
      return
    }

    const getNodes = async () => {
      const tags = await ServiceEditor.getAllTags(token)
      setAllTags(tags)

      let nodes = await ServiceEditor.searchInEditorTextContentByTags(token, {
        Tags: [...selectedTags],
        SearchText: searchForText,
      })

      // if (searchForText) {
      //   nodes = removeEmptyFoldersAndExpandRemaining(nodes)
      // }

      setNodes(nodes)
      //nodes = [...nodes, ...createDebugFolders()]

      setupFolderNodes(nodes, viewStyle)
    }

    getNodes()
  }, [token, reloadNodes, selectedTags, searchForText, viewStyle])

  useDidMountEffect(() => {
    const doStuff = async () => {
      if (!nodes) return
      await ensureNodeIsSaved()
      setupFolderNodes(nodes, viewStyle)
    }

    doStuff()
  }, [selectedTags, nodes, viewStyle])

  useDidMountEffect(() => {
    const doStuff = async () => {
      if (!nodes) return
      await ensureNodeIsSaved()
      setReloadNodes(x => x + 1)
    }

    doStuff()
  }, [searchForText])

  //const editor = useRef<any>(null)
  const editorRef = useRef<any>(null)
  const editorSubmittingRef = useRef<any>(null)

  const [changePassword, setChangePassword] = useState(false)

  //const editor = useRef(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [isUnauthorized, setIsUnauthorized] = useState(false)
  const [folderToBeDeleted, setFolderToBeDeleted] = useState<GUID | undefined>(undefined)
  const [folderToBeDeletedHasItems, setFolderToBeDeletedHasItems] = useState<GUID | undefined>(undefined)
  const [createFolderInParentId, setCreateFolderInParentId] = useState<GUID | null | undefined>(undefined)
  const [createItemInParentId, setCreateItemInParentId] = useState<GUID | null | undefined>(undefined)
  const [renameNodeId, setRenameNodeId] = useState<GUID | undefined>(undefined)
  const [nodeAsMoved, setNodeAsMoved] = useState<{ nodeId: GUID; newParentId: GUID | undefined }>()

  const [content, setContent] = useState('')
  const contentRef = useRef(content)
  const changeTimeoutRef = useRef<number | null>(null)
  const [contentIdIsChanged, setContentIdIsChangedInternal] = useState<GUID>()
  const setContentIdIsChanged = (newContentIdIsChanged: GUID | undefined) => {
    setContentIdIsChangedInternal(newContentIdIsChanged)
    contentIdIsChangedRef.current = newContentIdIsChanged
  }
  const contentIdIsChangedRef = useRef<GUID>()
  const [submitting, setSubmitting] = useState(false)
  const showOverlay = useDelayedLoading(submitting, 1000)
  useBeforeUnload(!!contentIdIsChanged)

  const [selectedFolderNode, setSelectedFolderNode] = useState<FolderNode | undefined>(undefined)
  const selectedFolderNodeRef = useRef<FolderNode | undefined>(undefined)

  const [showAttachmentsForNode, setShowAttachmentsForNode] = useState<GUID | undefined>(undefined)
  const [showHistoryForNode, setShowHistoryForNode] = useState<GUID | undefined>(undefined)
  const [editorAttachmentToBeRemoved, setEditorAttachmentToBeRemoved] = useState<IEditorAttachmentToBeRemoved>()

  useDidMountEffect(() => {
    lastOpenFolderNodesStore.set(getOpenNodeIds(folderNodes))
  }, [folderNodes])

  useEffect(() => {
    selectedFolderNodeRef.current = selectedFolderNode
    if (!selectedFolderNode) return

    const getContent = async () => {
      setSubmitting(true)
      try {
        const contentResp = await ServiceEditor.getEditorTextContent(token, selectedFolderNode.id)
        //await Util.sleep(5000)
        if (contentResp.Status.Id === DataResponseStatusIds.General_OK) {
          const ccc = contentResp.Data as string
          setContent(ccc)
          contentRef.current = ccc
          setContentIdIsChanged(undefined)
          setSubmitting(false)
        } else {
          setErrorMsg(`Error occured while loading content?! (${contentResp.Status.Id})`)
        }
      } catch (err) {
        if (err instanceof Unauthorized) {
          setIsUnauthorized(true)
          //return res.status(err.statusCode).send(err.message)
        } else {
          setErrorMsg('Error occured while loading content?!')
        }
      }
    }

    if (selectedFolderNode.children === undefined) {
      getContent()
    }

    lastOpenItemNodeStore.set(selectedFolderNode.id)

    //setContent(selectedNoneFolderNode.content ?? '')
  }, [selectedFolderNode])

  const focusEditor = (/*bookmark: any, caret: TextCaret | null*/) => {
    const jodit = editorRef.current
    if (!jodit) return

    // Jodit har typisk fokus-metoder, men den sikre er at fokusere contenteditable:
    jodit.editor?.focus?.()
  }

  //#region Save and auto focus previous area

  const bookmarkRef = useRef<any>(null)
  const shouldRestoreRef = useRef(false)
  const caretRef = useRef<TextCaret | null>(null)
  const doSave = useCallback(async () => {
    const jodit = editorRef.current
    const joditSubmitting = editorSubmittingRef.current
    if (!jodit || !joditSubmitting) return
    if (!contentIdIsChangedRef.current) return

    console.log(`Save on ${selectedFolderNodeRef.current?.id}`)
    if (!selectedFolderNodeRef.current?.id || selectedFolderNodeRef.current?.children !== undefined) return

    caretRef.current = getCaretIndex(jodit.editor) //, jodit)
    //console.log(`caretRef.current`, caretRef.current)

    bookmarkRef.current = jodit.s?.save?.() ?? jodit.selection.save()
    shouldRestoreRef.current = true

    const html = jodit.value
    joditSubmitting.value = html

    setErrorMsg('')
    setSubmitting(true)
    try {
      const resp = await ServiceEditor.addEditorText(token, {
        NodeId: selectedFolderNodeRef.current.id,
        Data: html,
      })

      setReloadNodes(x => x + 1)

      if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
        setErrorMsg(`Failed to save ${resp.Status.Id}`)
      } else {
        setContentIdIsChanged(undefined)
        requestAnimationFrame(() => {
          focusEditor()
        })
      }
    } catch (err) {
      if (err instanceof Unauthorized) {
        setIsUnauthorized(true)
      } else {
        setErrorMsg(`Failed to save`)
      }
    } finally {
      //setTimeout(() => {
      setSubmitting(false)
      //}, 3000)
    }
  }, [token])

  //#endregion Save and auto focus previous area

  const [validateUnlock, setValidateUnlock] = useState<GUID>()
  const doToggleLock = async (nodeId: GUID | undefined) => {
    const selectedNoneFolderNode = nodes?.find(n => n.Id === nodeId)
    if (!selectedNoneFolderNode) return

    setErrorMsg('')
    setSubmitting(true)
    try {
      const resp = await ServiceEditor.addUpdateEditorNode(token, {
        EditorNode: {
          ...selectedNoneFolderNode,
          Locked: !selectedNoneFolderNode.Locked,
        },
      })

      if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
        setErrorMsg(`Failed to lock ${resp.Status.Id}`)
      } else {
        setReloadNodes(x => x + 1)
      }
    } catch (err) {
      if (err instanceof Unauthorized) {
        setIsUnauthorized(true)
      } else {
        setErrorMsg(`Failed to lock`)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const ensureNodeIsSaved = async () => {
    if (!selectedFolderNodeRef.current?.id) return
    const selectedFolderNode = nodes?.find(n => n.Id === selectedFolderNodeRef.current?.id)
    if (!selectedFolderNode || selectedFolderNode.Type === EditorNodeType.Folder) return

    if (!selectedFolderNode.Locked) {
      await doSave()
    }
  }

  const doTryToggleLock = async () => {
    if (!selectedFolderNodeRef.current?.id) return
    const selectedFolderNode = nodes?.find(n => n.Id === selectedFolderNodeRef.current?.id)
    if (!selectedFolderNode || selectedFolderNode.Type === EditorNodeType.Folder) return

    console.log(`doTryToggleLock :: reloadNodes=${reloadNodes}`)
    await ensureNodeIsSaved()

    if (selectedFolderNode.Locked) {
      setValidateUnlock(selectedFolderNode.Id)
    } else {
      doToggleLock(selectedFolderNode.Id)
    }
  }

  //const xxx = (Jodit as any).defaultOptions.buttons.filter((x: any) => x !== '---')
  //console.log(xxx)
  ;(Jodit as any).defaultOptions.controls.save = {
    tooltip: 'Save',
    icon: 'save',
    exec: async (jodit: any) => {
      //console.log(jodit)
      //console.log(jodit.value)
      doSave()
      //setContent('<p>asdasdasd</p>')
      //setShowSaveDialog(true)
      // din logik her
    },
  }

  const { ref: logoRef, dimensions: logoDimensions } = useElementDimensions<HTMLDivElement>()
  const { ref: tagsRef, dimensions: tagsDimensions } = useElementDimensions<HTMLDivElement>()
  const { ref: searchRef, dimensions: searchDimensions } = useElementDimensions<HTMLDivElement>()
  const { ref: changeViewRef, dimensions: changeViewDimensions } = useElementDimensions<HTMLDivElement>()
  const { ref: headerRef, dimensions: headerDimensions } = useElementDimensions<HTMLDivElement>()

  const primedRef = useRef(false)

  const handleEditorRef = useCallback(
    (editor: IJodit) => {
      if (primedRef.current) return
      primedRef.current = true

      // editorRef.current = editor
      // console.log('handleEditorRef.........')
      // console.log(editor)
      //console.log(editor.editor.getHTML)

      const prev = document.activeElement as HTMLElement | null

      editor.focus() // "primer" selection så table-knappen ikke crasher

      //#region Patch with token
      // Patch window.open so it replaces [[[TOKEN]]] everywhere (incl. "Open link")
      const ow: any = (editor as any).ownerWindow ?? (editor as any).ow ?? window
      const originalOpen = ow.open?.bind(ow)

      if (typeof originalOpen === 'function') {
        ow.open = (url: any, target?: any, features?: any) => {
          if (typeof url === 'string' && url.includes('[[[TOKEN]]]')) {
            let realUrl = url.replace('[[[BASEURL]]]', Consts.defaultBaseUrl)
            realUrl = realUrl.replace('[[[TOKEN]]]', encodeURIComponent(token))
            return originalOpen(realUrl, target, features)
          }
          return originalOpen(url, target, features)
        }

        // optional cleanup reference
        ;(editor as any).__restoreOpen = () => {
          ow.open = originalOpen
        }
      }
      //#endregion Patch with token

      // Robust Icon lookup (afhænger af Jodit build/version)
      const Icon = (editor as any)?.constructor?.modules?.Icon ?? (editor as any)?.constructor?.Icon
      if (!Icon?.set) {
        // Fail silently or log once
        console.warn('Jodit Icon module not found on editor.constructor')
        queueMicrotask(() => prev?.focus?.())
        return
      }

      Icon.set(
        'showAttachments',
        `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M7 3h10v4H7V3zm-2 6h10v12H5V9zm12-2h2v14H9v-2h8V7z"/>
    </svg>`,
      )

      Icon.set(
        'showHistory',
        `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M13 3a9 9 0 1 0 8.94 8H19a7 7 0 1 1-2.05-4.95L15 8h6V2l-2.24 2.24A8.96 8.96 0 0 0 13 3zm-1 5h2v5l4 2-1 1.73L12 14V8z"/>
  </svg>`,
      )

      Icon.set(
        'lock',
        `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M17 10h-1V8a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-7-2a2 2 0 1 1 4 0v2h-4V8zm7 12H7v-8h10v8z"/>
    </svg>`,
      )

      Icon.set(
        'unlock',
        `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M17 10h-7V8a2 2 0 1 1 4 0h2a4 4 0 1 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm0 10H7v-8h10v8z"/>
    </svg>`,
      )

      queueMicrotask(() => prev?.focus?.())

      //setupOnClickEvent(editor, token)
    },
    [token, selectedFolderNode],
  )

  const [openSettingsMenu, setOpenSettingsMenu] = useState(false)
  const settingsMenuItems = [
    // {
    //   id: 'prefs',
    //   label: 'Preferences',
    //   icon: '⚙️',
    //   onSelect: () => console.log('prefs'),
    // },
    {
      id: 'changePassword',
      label: 'Change password',
      icon: '🔒',
      onSelect: () => setChangePassword(true),
    },
    // {
    //   kind: 'separator' as const,
    //   id: 'sep',
    // },
    {
      id: 'logout',
      label: 'Log out',
      icon: '👋',
      danger: true,
      onSelect: () => onLogout?.(),
    },
  ]

  const configNotSubmitting = useMemo(() => {
    console.log('build new configNotSubmitting')
    return buildEditorConfig(
      editorRef,
      token,
      height - headerDimensions.height,
      selectedFolderNode,
      nodes,
      false,
      setShowAttachmentsForNode,
      setShowHistoryForNode,
      doTryToggleLock,
      setErrorMsg,
      doSave,
    )
  }, [height, headerDimensions.height, selectedFolderNode /*, nodes*/]) // Hvorfor er det vigtigt at opdatere denne hvis nodes er ny? For det ødelægger cursor-placering. Det har nok noget med kald til doTryToggleLock

  const configSubmitting = useMemo(() => {
    return buildEditorConfig(
      editorSubmittingRef,
      token,
      height - headerDimensions.height,
      selectedFolderNode,
      nodes,
      true,
      setShowAttachmentsForNode,
      setShowHistoryForNode,
      doTryToggleLock,
      setErrorMsg,
      doSave,
    )
  }, [height, headerDimensions.height, selectedFolderNode /*, nodes, reloadNodes*/]) // Hvorfor er det vigtigt at opdatere denne hvis nodes er ny? For det ødelægger cursor-placering

  // console.log('selectedNoneFolderNode?.isLocked', selectedNoneFolderNode?.isLocked)

  const editorNotSubmitting = useMemo(() => {
    // console.log(`new editor!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`, selectedNoneFolderNode?.id)
    //function Editor({ initialHtml, onDirtyRef }: { initialHtml: string; onDirtyRef: React.MutableRefObject<string> }) {
    return (
      <JoditEditor
        key={`wtfns${selectedFolderNode?.id ?? ''}`}
        ref={handleEditorRef}
        value={content}
        config={configNotSubmitting}
        tabIndex={1}
        onChange={(newContent: string) => {
          if (!contentIdIsChangedRef.current) {
            setContentIdIsChanged(newContent !== contentRef.current ? selectedFolderNode?.id : undefined)
          } else {
            if (changeTimeoutRef.current) window.clearTimeout(changeTimeoutRef.current)

            changeTimeoutRef.current = window.setTimeout(() => {
              setContentIdIsChanged(newContent !== contentRef.current ? selectedFolderNode?.id : undefined)
            }, 300)
          }
        }}
        // onBlur={handleBlur}
        //onChange={handleChange}
      />
    )
    // return (
    //   <JoditEditor
    //     ref={editorRef}
    //     value={initialHtml} // må kun ændres ved node-skift
    //     onChange={v => {
    //       onDirtyRef.current = v
    //     }}
    //   />
    // )
  }, [selectedFolderNode, content, selectedFolderNode?.isLocked, nodes, reloadNodes, height, headerDimensions.height]) // Crazy selvom reloadNodes jo hænger sammen med nodes, så får jeg kun den nye nodes med i flowet hvis den indgår som dependency

  useEffect(() => {
    return () => {
      if (changeTimeoutRef.current) window.clearTimeout(changeTimeoutRef.current)
    }
  }, [])

  const editorSubmitting = useMemo(() => {
    //console.log(`new editor!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`, selectedNoneFolderNode?.id)
    //function Editor({ initialHtml, onDirtyRef }: { initialHtml: string; onDirtyRef: React.MutableRefObject<string> }) {

    return (
      <JoditEditor
        key={`wtfs${selectedFolderNode?.id ?? ''}`}
        ref={editorSubmittingRef}
        config={configSubmitting}
        tabIndex={1}
        // onBlur={handleBlur}
        //onChange={handleChange} html is read directly from editor...
      />
    )
  }, [selectedFolderNode, content, selectedFolderNode?.isLocked, height, headerDimensions.height])

  return (
    <>
      <SplitView
        onChangeLeftWidth={leftWidth => {
          if (persistLeftWidthRef.current) window.clearTimeout(persistLeftWidthRef.current)

          persistLeftWidthRef.current = window.setTimeout(() => {
            setPersistLeftWidth(leftWidth)
          }, 700)
        }}
        left={
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div ref={logoRef} style={{ display: 'flex', width: '100%', alignContent: 'center', justifyContent: 'center', paddingTop: 5 }}>
              <img src="/icons/woonotes/woonotes462wide.png" style={{ width: '90%', maxWidth: 400, marginBottom: 10 }} />
            </div>
            <div
              ref={tagsRef}
              style={{
                display: 'flex',
                width: '100%',
                alignContent: 'center',
                justifyContent: 'center',
                paddingTop: 5,
                marginLeft: -10,
                marginTop: -10,
                flexWrap: 'wrap',
              }}
            >
              {allTags.map(tag => {
                return (
                  <div key={`gtag${tag}`} style={{ marginLeft: 20, marginTop: 10 }}>
                    <WooNotesButton
                      raised={false}
                      size={WooNotesButtonSize.Tiny}
                      color={selectedTags.has(tag) ? WooNotesButtonColor.Green : WooNotesButtonColor.White}
                      onClick={() =>
                        setSelectedTags(selectedTags => {
                          if (selectedTags.has(tag)) {
                            return Util.pureSetDelete(selectedTags, tag)
                          }
                          return Util.pureSetAdd(selectedTags, tag)
                        })
                      }
                    >
                      {tag}
                    </WooNotesButton>
                  </div>
                )
              })}
            </div>
            <div
              ref={searchRef}
              style={{
                display: 'flex',
                width: '100%',
                alignContent: 'center',
                justifyContent: 'center',
                paddingTop: 5,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <WooNotesTextInput
                  label={''}
                  placeholder="Search for text..."
                  raised={false}
                  onChange={e => {
                    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current)

                    searchTimeoutRef.current = window.setTimeout(() => {
                      setSearchForText(e.target.value)
                    }, 700)
                  }}
                />
              </div>
            </div>
            <div
              ref={changeViewRef}
              style={{
                display: 'flex',
                width: '100%',
                alignContent: 'center',
                justifyContent: 'center',
                paddingTop: 5,
                flexWrap: 'wrap',
              }}
            >
              <WooNotesButton
                size={WooNotesButtonSize.Tiny}
                color={WooNotesButtonColor.Green}
                onClick={() => {
                  changeViewStyle()
                }}
              >
                {viewStyle === 'sortedDesc' ? 'Sorted by change date' : 'By folder'}
              </WooNotesButton>
            </div>
            <FolderExplorer
              canNavigate={!contentIdIsChanged}
              canCreateNodes={!searchForText}
              onSelectFolder={nodeId => {
                if (!!contentIdIsChanged) return
                setFolderNodes(e => {
                  const x = selectFolderNode(e, nodeId)
                  return x
                })
                setSelectedFolderNode(getNodeById(folderNodes, nodeId))
              }}
              onSelectText={nodeId => {
                if (!!contentIdIsChanged) return
                setFolderNodes(e => {
                  const x = selectFolderNode(e, nodeId)
                  return x
                })
                setSelectedFolderNode(getNodeById(folderNodes, nodeId))
              }}
              onCreateFolder={async parentId => {
                if (!!contentIdIsChanged) return
                setCreateFolderInParentId(parentId)
              }}
              onCreateText={async parentId => {
                if (!!contentIdIsChanged) return
                setCreateItemInParentId(parentId)
              }}
              onRemoveNode={async nodeId => {
                if (!!contentIdIsChanged) return
                setFolderToBeDeleted(nodeId)
              }}
              onRenameNode={nodeId => {
                if (!!contentIdIsChanged) return
                setRenameNodeId(nodeId)
              }}
              onToggleNode={async nodeId => {
                console.log(`onToggleNode...`)
                setFolderNodes(e => {
                  const x = toggleFolderNode(e, nodeId)
                  console.log(x)
                  return x
                })
              }}
              onMoveNode={(nodeId, newParentId) => {
                if (!!contentIdIsChanged) return
                if (!folderNodes) return
                if (getNodeById(folderNodes, nodeId)?.parentId !== newParentId) {
                  // console.log(`nodeId`, nodeId)
                  // console.log(`newParentId`, newParentId)
                  setNodeAsMoved({ nodeId, newParentId })
                }
              }}
              nodes={folderNodes}
              nodeIdChanged={contentIdIsChanged}
              defaultSelectedNodeId={defaultSelectedNodeId}
              marginBottom={
                /*buttonsDimensions?.height +*/ 5 * 4 /*margins*/ +
                logoDimensions.height +
                tagsDimensions.height +
                searchDimensions.height +
                changeViewDimensions.height
              }
              viewStyle={viewStyle}
            />
          </div>
        }
        right={
          <div style={{ position: 'relative' }}>
            <div ref={headerRef} style={{ display: 'flex', position: 'relative', width: '100%' }}>
              <div
                style={{
                  display: 'block',
                  flex: 1,
                  fontWeight: 'bold',
                  fontSize: 40,
                  marginLeft: 5,
                  // overflow: 'hidden',
                  // textOverflow: 'ellipsis',
                  // whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: 'block',
                    flex: 1,
                    fontWeight: 'bold',
                    fontSize: 40,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    color: 'black',
                  }}
                >
                  {selectedFolderNode?.name}
                </div>
                <div
                  style={{
                    display: 'block',
                    flex: 1,
                    fontWeight: 'bold',
                    fontSize: 20,
                    marginLeft: 5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    color: 'gray',
                  }}
                >
                  ... in {buildNodePath(selectedFolderNode?.id)}
                </div>
              </div>
              <div style={{ display: 'flex', flexShrink: 0 }}>
                <div style={{ display: 'flex', paddingBottom: 8, height: 50 }}>
                  <WooNotesButton
                    color={WooNotesButtonColor.Blue}
                    onClick={() => {
                      setOpenSettingsMenu(x => !x)
                    }}
                    size={WooNotesButtonSize.Slim}
                    style={{ fontSize: 20, padding: '3px 5px', margin: 5 }}
                    //raised={false}
                  >
                    ⚙️
                  </WooNotesButton>
                  <WooNotesPopupMenu
                    open={openSettingsMenu}
                    onClose={() => setOpenSettingsMenu(false)}
                    anchor="bottom-right"
                    caretOffsetPx={0}
                    title="WooNotes"
                    items={settingsMenuItems}
                  />
                </div>
              </div>
            </div>
            <div style={{ position: 'relative', opacity: selectedFolderNode ? 1 : 0, width: '100%', height: height - headerDimensions.height }}>
              <div
                style={{
                  position: 'absolute',
                  inset: 1,
                  opacity: selectedFolderNode && selectedFolderNode?.children === undefined ? 1 : 0,
                  pointerEvents: submitting ? 'auto' : 'none',
                }}
              >
                {editorSubmitting}
              </div>
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  inset: 1,
                  opacity: selectedFolderNode?.children !== undefined ? 1 : 0,
                  padding: 10,
                  gap: 10,
                  maxWidth: 300,
                }}
              >
                {selectedFolderNode?.children?.map(child => {
                  return (
                    <div key={`o${child.id}`} style={{ display: 'flex', gap: 5 }}>
                      {FolderNodeUI(child)}
                    </div>
                  )
                })}
              </div>
              <div
                style={{
                  position: 'absolute',
                  inset: 1,
                  opacity: selectedFolderNode && selectedFolderNode?.children === undefined && !submitting ? 1 : 0,
                  pointerEvents: selectedFolderNode?.children === undefined ? 'auto' : 'none',
                }}
              >
                {editorNotSubmitting}
              </div>
            </div>

            {nodesRead && !selectedFolderNode && (
              <div style={{ position: 'absolute', inset: 0, alignContent: 'center', justifyItems: 'center', pointerEvents: 'none' }}>
                <div style={{ color: 'black', fontWeight: 'bold', fontSize: '2vw' }}>Nothing selected</div>
              </div>
            )}

            <ConfirmRemoveFolderDialog
              folderNodeId={folderToBeDeleted ?? folderToBeDeletedHasItems}
              folderNodes={folderNodes}
              folderHasItems={!!folderToBeDeletedHasItems}
              onOK={async () => {
                setSubmitting(true)
                try {
                  if (!folderToBeDeleted) return

                  const resp = await ServiceEditor.removeEditorNode(token, {
                    NodeId: folderToBeDeleted,
                    Force: !!folderToBeDeletedHasItems,
                  })

                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    if (resp.Status.Id === DataResponseStatusIds.Editor_FolderHasItems) {
                      setFolderToBeDeletedHasItems(folderToBeDeleted)
                    } else {
                      setErrorMsg(`Failed to remove folder ${resp.Status.Id}`)
                    }
                  } else {
                    setSubmitting(false)
                    setFolderToBeDeleted(undefined)
                    setFolderToBeDeletedHasItems(undefined)
                    setReloadNodes(x => x + 1)
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while removing folder?!')
                  }
                }
              }}
              onCancel={async () => {
                setFolderToBeDeleted(undefined)
                setFolderToBeDeletedHasItems(undefined)
              }}
            />
            <RenameNodeDialog
              nodeId={renameNodeId}
              folderNodes={folderNodes}
              onSave={async name => {
                if (!renameNodeId) return
                const apiNode = getAPINodeById(nodes, renameNodeId)
                if (!apiNode) return

                setSubmitting(true)
                try {
                  const resp = await ServiceEditor.addUpdateEditorNode(token, {
                    EditorNode: {
                      ...apiNode,
                      Name: name,
                    },
                  })

                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Failed to rename node ${resp.Status.Id}`)
                  } else {
                    setRenameNodeId(undefined)
                    setSubmitting(false)
                    setReloadNodes(x => x + 1)
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while renaming node?!')
                  }
                }
              }}
              onCancel={async () => {
                setRenameNodeId(undefined)
              }}
            />
            <CreateFolderNodeDialog
              parentNodeId={createFolderInParentId}
              folderNodes={folderNodes}
              onSave={async name => {
                setSubmitting(true)
                try {
                  const newEditorNode = {
                    Id: Util.UUID,
                    Name: name,
                    Deleted: false,
                    Locked: false,
                    BeforeNodeId: undefined,
                    Description: '',
                    ParentNodeId: createFolderInParentId ?? undefined,
                    PlayerId: EmptyId,
                    Type: EditorNodeType.Folder,
                    CreatedTimestamp: new Date(),
                    UpdatedTimestamp: new Date(),
                    DeletedTimestamp: undefined,
                    LastEditTextTimestamp: new Date(),
                    Tags: [],
                  }

                  const resp = await ServiceEditor.addUpdateEditorNode(token, {
                    EditorNode: newEditorNode,
                  })

                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Failed to create folder ${resp.Status.Id}`)
                  } else {
                    setSubmitting(false)
                    setCreateFolderInParentId(undefined)
                    setReloadNodes(x => x + 1)
                    const newFolderNode = mapEditorNodeToFolderNodeBase(newEditorNode)
                    setSelectedFolderNode(newFolderNode)
                    selectedFolderNodeRef.current = newFolderNode
                    focusEditor()
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while creating folder?!')
                  }
                }
              }}
              onCancel={async () => {
                setCreateFolderInParentId(undefined)
              }}
            />
            <CreateItemNodeDialog
              parentNodeId={createItemInParentId}
              folderNodes={folderNodes}
              onSave={async name => {
                setSubmitting(true)
                try {
                  const newEditorNode = {
                    Id: Util.UUID,
                    Name: name,
                    Deleted: false,
                    Locked: false,
                    BeforeNodeId: undefined,
                    Description: '',
                    ParentNodeId: createItemInParentId ?? undefined,
                    PlayerId: EmptyId,
                    Type: EditorNodeType.Text,
                    CreatedTimestamp: new Date(),
                    UpdatedTimestamp: new Date(),
                    DeletedTimestamp: undefined,
                    LastEditTextTimestamp: new Date(),
                  } as IEditorNode

                  const resp = await ServiceEditor.addUpdateEditorNode(token, {
                    EditorNode: newEditorNode,
                  })

                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Failed to create item ${resp.Status.Id}`)
                  } else {
                    setSubmitting(false)
                    setCreateItemInParentId(undefined)
                    setReloadNodes(x => x + 1)
                    const newFolderNode = mapEditorNodeToFolderNodeBase(newEditorNode)
                    setSelectedFolderNode(newFolderNode)
                    selectedFolderNodeRef.current = newFolderNode
                    focusEditor()
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while creating item?!')
                  }
                }
              }}
              onCancel={async () => {
                setCreateItemInParentId(undefined)
              }}
            />
            <NodeAttachmentsDialog
              token={token}
              nodeId={showAttachmentsForNode}
              folderNodes={folderNodes}
              onCancel={() => setShowAttachmentsForNode(undefined)}
              onError={setErrorMsg}
              onInsertAttachment={(nodeId, editorAttachment) => {
                var editor = editorRef.current
                console.log(editor.selection)
                const url = `[[[BASEURL]]]/editor/getEditorAttachmentData?hmm=1339&editorAttachmentId=${editorAttachment.Id}&authorizationDeviceId=Editor&authorizationToken=[[[TOKEN]]]&nodeId=${editorAttachment.NodeId}`
                editor.selection.insertHTML(`<a href="${url}" target="_blank" rel="noopener">${Util.htmlEncode(editorAttachment.Filename)}</a>`)
              }}
              onRemoveAttachment={(nodeId, editorAttachment) => {
                setEditorAttachmentToBeRemoved({ nodeId, editorAttachment })
              }}
              editorAttachmentToBeRemoved={editorAttachmentToBeRemoved}
            />
            <NodeHistoryDialog
              token={token}
              nodeId={showHistoryForNode}
              folderNodes={folderNodes}
              onCancel={() => setShowHistoryForNode(undefined)}
              onError={setErrorMsg}
            />
            <ConfirmRemoveAttachmentDialog
              nodeId={editorAttachmentToBeRemoved?.nodeId}
              editorAttachment={editorAttachmentToBeRemoved?.editorAttachment}
              folderNodes={folderNodes}
              onOK={async () => {
                setSubmitting(true)
                try {
                  if (!editorAttachmentToBeRemoved || !editorAttachmentToBeRemoved.editorAttachment) return
                  const resp = await ServiceEditor.removeEditorAttachment(token, {
                    NodeId: editorAttachmentToBeRemoved.nodeId,
                    EditorAttachmentId: editorAttachmentToBeRemoved.editorAttachment.Id,
                  })
                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Error occured while removing attachment (${resp.Status.Id})`)
                  } else {
                    setSubmitting(false)
                    setEditorAttachmentToBeRemoved(undefined)
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while removing attachment?!')
                  }
                } finally {
                  setSubmitting(false)
                }
              }}
              onCancel={() => {
                setEditorAttachmentToBeRemoved(undefined)
              }}
            />
            <ChangePasswordDialog
              player={changePassword ? player : undefined}
              onCancel={() => setChangePassword(false)}
              onSave={async (player, password) => {
                try {
                  const resp = await ServicePlayer.updatePlayer(token, {
                    ...player,
                    Add: false,
                    Password: password,
                  })
                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Error occured while changing password (${resp.Status.Id})`)
                  } else {
                    setSubmitting(false)
                    setChangePassword(false)
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg(`Error occured while changing password?!`)
                  }
                } finally {
                  setSubmitting(false)
                }
              }}
            />
            <ConfirmSetFolderItemUnlockedDialog
              folderNodeId={validateUnlock}
              folderNodes={folderNodes}
              onOK={() => {
                setValidateUnlock(undefined)
                doToggleLock(validateUnlock)
              }}
              onCancel={() => setValidateUnlock(undefined)}
            />
            <ConfirmMoveNodeDialog
              moveNode={nodeAsMoved}
              folderNodes={folderNodes}
              onOK={async () => {
                if (!nodeAsMoved?.nodeId) return
                const apiNode = getAPINodeById(nodes, nodeAsMoved.nodeId)
                if (!apiNode) return

                setSubmitting(true)
                try {
                  const resp = await ServiceEditor.addUpdateEditorNode(token, {
                    EditorNode: {
                      ...apiNode,
                      ParentNodeId: nodeAsMoved.newParentId,
                    },
                  })

                  if (resp.Status.Id !== DataResponseStatusIds.General_OK) {
                    setErrorMsg(`Failed to move node ${resp.Status.Id}`)
                  } else {
                    setNodeAsMoved(undefined)
                    setSubmitting(false)
                    setReloadNodes(x => x + 1)
                  }
                } catch (err) {
                  if (err instanceof Unauthorized) {
                    setIsUnauthorized(true)
                  } else {
                    setErrorMsg('Error occured while moving node?!')
                  }
                }
              }}
              onCancel={() => setNodeAsMoved(undefined)}
            />
            <IsUnauthorizedDialog
              isUnauthorized={isUnauthorized}
              onLogin={(token, player) => {
                setToken(token)
                setPlayer(player)
                if (token && player) {
                  setIsUnauthorized(false)
                }
              }}
            />
            <ErrorDialog errorMsg={errorMsg} onClose={() => setErrorMsg('')} />
          </div>
        }
        initialLeftWidth={persistLeftWidth}
        minLeftWidth={220}
        maxLeftWidth={700}
      />
      <LoaderOverlay visible={showOverlay} />
    </>
  )
}

export default WooEditorAndNodes
